// DashboardSummaryWidget.jsx
import React, { useMemo, forwardRef, useImperativeHandle } from "react";
import {
  dashboardBuildings,
  dashboardMoney,
  dashboardWantListings,
  dashboardTime,
} from "@/images";
import { useAgentDashboardMetrics } from "@/hooks";
import { thousandSeperatedValue } from "@/helpers/v2/common";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";

const DashboardSummaryWidget = forwardRef(({ scope = "inventory" }, ref) => {

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();

  const { data, loading, refetch } = useAgentDashboardMetrics(scope);

  useImperativeHandle(ref, () => ({ refetch }), [refetch]);

  const items = useMemo(() => {
    const isTwoItemScope = scope === "inventory" || scope === "mls";
    if (loading)
      return isTwoItemScope ? Array(2).fill({}) : Array(3).fill({});

    if (!data) return [];

    if (isTwoItemScope)
      return [
        {
          title: "Potential Seller Home Values",
          value:
            data.potentialSellerValue != null
              ? `$${Number(data.potentialSellerValue).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}`
              : "-",
          circleClass: "dashboard-circle-green",
          img: dashboardMoney,
        },
        {
          title: "Total Buyer Matches",
          value: thousandSeperatedValue(data.totalBuyerMatches) ?? "-",
          circleClass: "dashboard-circle-green",
          img: dashboardBuildings,
        },
      ];

    return [
      {
        title: shouldRestrictAgent ? "Syndicated Active Buyer Pipeline" : "Active Buyer Pipeline",
        value:
          data.activeBuyerPipeline != null
            ? `$${Number(data.activeBuyerPipeline).toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`
            : "-",
        circleClass: "dashboard-circle-green",
        img: dashboardMoney,
      },
      {
        title: shouldRestrictAgent ? "Syndicated Active Want Listings" : "Active Want Listings",
        value: data.currentWantListings ?? "-",
        circleClass: "dashboard-circle-green",
        img: dashboardWantListings,
      },
      {
        title: "Forms Waiting Review",
        value: data.formsWaitingReview ?? "-",
        circleClass: "dashboard-circle-orange",
        img: dashboardTime,
      },
    ];
  }, [loading, data, scope]);

  return (
    <div className="dashboard-summary-widget d-flex justify-content-between align-items-center p-4">
      {items.map(({ title, value, circleClass, img }, index) => (
        <div
          key={title || index}
          className="summary-item d-flex align-items-center flex-grow-1"
        >
          <div
            className={`icon-wrapper me-3 ${loading ? "dashboard-placeholder-circle" : circleClass}`}
          >
            {!loading && (
              <img src={img} className="dashboard-summary-icon" alt="" />
            )}
          </div>
          <div className="text-wrapper w-100">
            <h6 className="mb-1 dashboard-text-secondary">
              {loading ? (
                <div className="dashboard-placeholder-line w-75" />
              ) : (
                title
              )}
            </h6>
            <h3 className="mb-1 dashboard-text-tertiary">
              {loading ? (
                <div className="dashboard-placeholder-line w-50" />
              ) : (
                value
              )}
            </h3>
          </div>
        </div>
      ))}
    </div>
  );
});

export default DashboardSummaryWidget;
